/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { AxiosError } from 'axios';
import { ErrorBody } from 'src/core/types/ErrorBody';
import { createBaseErrorHandler } from 'src/core/utils/interceptors/errorHandlers/createBaseErrorHandler';

/**
 * Represents a handler for error objects that are returned from
 * a LUIS API.
 */
export const createApiErrorHandler = () => {
    const baseActions = createBaseErrorHandler();

    return {
        ...baseActions,

        /**
         * Handles the error response returned from a LUIS API.
         *
         * @param error The error object thrown.
         * @returns A formatted Error object.
         */
        handle: async (error: AxiosError<{ error: ErrorBody }>) => {
            if (!error.response) {
                return baseActions.delegate(error);
            }

            const jsonError = error.response.data?.error;
            const errorObject: Error = new Error();

            if (typeof jsonError !== 'object' || !('code' in jsonError && 'message' in jsonError)) {
                return baseActions.delegate(error);
            }

            errorObject.name = `${error.response.status}`;
            let message = `${jsonError.code}: ${jsonError.message}`;

            if (jsonError.details?.length) {
                jsonError.details.forEach(err => {
                    message = message.concat(`\n - ${err.target ?? ''} ${err.code}: ${err.message}`);
                });
            }

            // To be revisited after CT Runtime fix it at their side
            if (jsonError.innererror) {
                message = message.concat(`\n - ${jsonError.innererror.code}: ${jsonError.innererror.message}`);
            }

            if (jsonError.innerError) {
                message = message.concat(`\n - ${jsonError.innerError.code}: ${jsonError.innerError.message}`);
            }

            errorObject.message = message;

            return errorObject;
        }
    };
};
